import { useAuthStore } from '@/stores/auth-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { userApi } from '@/lib/api/user';
import { teamApi } from '@/lib/api/team';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getAuthInstance } from '@/lib/firebase';
import { updateProfile, type User as FirebaseUser } from 'firebase/auth';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  COMPANY_SIZES,
  INDUSTRIES,
  USER_ROLES,
  USE_CASES,
  HOW_DID_YOU_HEAR
} from '@/components/onboarding/OnboardingFormFields';
import Loader from '@/components/ui/loader';

const CORE_API_URL = import.meta.env.VITE_CORE_API_BASE_URL ?? 'http://localhost:8080';

type StepId = 1 | 2 | 3;

function generateTeamKey(name: string): string {
  const key = name
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 3);
  // Backend requires 2-3 uppercase alphanumeric characters.
  // Ensure we always return something valid even for very short/invalid names.
  return key.length >= 2 ? key : 'TM';
}

function isValidTeamKey(key: string): boolean {
  return /^[A-Z0-9]{2,3}$/.test(key);
}

export function CreateWorkspacePage() {
  const navigate = useNavigate();
  const createWorkspace = useWorkspaceStore((s) => s.create);
  const currentUser = useAuthStore((s) => s.user) as FirebaseUser | null;
  const authState = useAuthStore((s) => s.authState);
  const initAuth = useAuthStore((s) => s.init);

  const [step, setStep] = useState<StepId>(1);
  const [workspaceName, setWorkspaceName] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [role, setRole] = useState('');
  const [useCaseInput, setUseCaseInput] = useState('');
  const [howDidYouHear, setHowDidYouHear] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [industry, setIndustry] = useState('');

  const [workspaceId, setWorkspaceId] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);

  const [integrationLoading, setIntegrationLoading] = useState<string | null>(null);

  const defaultTeamKeyForWorkspace = useMemo(
    () => generateTeamKey(workspaceName.trim()),
    [workspaceName]
  );

  useEffect(() => {
    if (!currentUser?.displayName) return;

    if (!userDisplayName) {
      setUserDisplayName(currentUser.displayName);
    }

    // Default "company" name (workspace name) on first visit.
    if (!workspaceName) {
      setWorkspaceName(`${currentUser.displayName}'s Workspace`);
    }
  }, [currentUser?.displayName, userDisplayName, workspaceName]);

  const steps = useMemo(
    () => [
      { id: 1 as const, label: 'Workspace' },
      { id: 2 as const, label: 'Your details' },
      { id: 3 as const, label: 'Integrations' }
    ],
    []
  );

  // Ensure Firebase auth listener is started on this public route,
  // so the `apiClient` receives the bearer token.
  useEffect(() => {
    initAuth();
  }, []);

  const handleStep1Submit = async (e: FormEvent) => {
    e.preventDefault();

    if (authState !== 'signed-in' || !currentUser) {
      toast.error('Please sign in again.');
      return;
    }

    const wsName = workspaceName.trim();
    if (!wsName) return;

    // First team defaults:
    // - team name matches the workspace name
    // - ticket prefix key is generated from the workspace name
    const desiredTeamName = wsName;
    const desiredTeamKey = defaultTeamKeyForWorkspace;

    if (!isValidTeamKey(desiredTeamKey)) {
      toast.error('Team key must be 2-3 uppercase alphanumeric characters (e.g. BUG, SUP).');
      return;
    }

    setSubmitting(true);
    try {
      const workspace = await createWorkspace({
        name: wsName,
        companySize: companySize || undefined,
        industry: industry || undefined
      });

      const teamsRes = await teamApi.getTeams(workspace.id);
      if (!teamsRes.success) throw new Error(teamsRes.error ?? 'Failed to load teams');

      const teams = teamsRes.data ?? [];
      const defaultTeam = teams.find((t) => t.key === defaultTeamKeyForWorkspace) ?? teams[0];

      if (!defaultTeam) throw new Error('Default team not found');

      await teamApi.updateTeam(workspace.id, defaultTeam.id, {
        name: desiredTeamName,
        key: desiredTeamKey
      });

      setWorkspaceId(workspace.id);
      setStep(2);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStep2Submit = async (e: FormEvent) => {
    e.preventDefault();

    if (!workspaceId) return;

    const displayName = userDisplayName.trim();
    if (!displayName) {
      toast.error('Please enter your display name.');
      return;
    }

    setSubmitting(true);
    try {
      // Keep Firebase auth profile in sync so profile UI matches immediately.
      if (currentUser) {
        const auth = getAuthInstance();
        if (auth) {
          await updateProfile(currentUser, { displayName });
        }
      }

      const userRes = await userApi.updateMe({
        displayName,
        role: role || null,
        useCase: useCaseInput || null,
        howDidYouHear: howDidYouHear || null
      });
      if (!userRes.success) throw new Error(userRes.error ?? 'Failed to save your profile');

      setStep(3);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save your details');
    } finally {
      setSubmitting(false);
    }
  };

  const startSlack = () => {
    if (!workspaceId) return;
    setIntegrationLoading('slack');
    window.open(`${CORE_API_URL}/api/auth/slack?workspace_id=${workspaceId}`, '_blank');
    setIntegrationLoading(null);
  };

  const startGithub = () => {
    if (!workspaceId) return;
    setIntegrationLoading('github');
    window.open(`${CORE_API_URL}/api/auth/github?workspace_id=${workspaceId}`, '_blank');
    setIntegrationLoading(null);
  };

  const startDiscord = async () => {
    if (!workspaceId) return;
    setIntegrationLoading('discord');
    try {
      const idToken = currentUser ? await currentUser.getIdToken() : null;
      const res = await fetch(
        `${CORE_API_URL}/api/auth/discord/invite-url?workspace_id=${workspaceId}`,
        idToken ? { headers: { Authorization: `Bearer ${idToken}` } } : undefined
      );
      const data = (await res.json()) as { url?: string; error?: string };
      if (data.url) window.open(data.url, '_blank');
      else toast.error(data.error ?? 'Failed to generate Discord invite URL');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to generate Discord invite URL');
    } finally {
      setIntegrationLoading(null);
    }
  };

  const finish = () => {
    if (!workspaceId) return;
    navigate(`/workspace/${workspaceId}`);
  };

  const stepTitle =
    step === 1 ? 'Setup Your Workspace' : step === 2 ? 'Setup Your Profile' : "What's Your Stack?";

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <div className="flex flex-col items-center justify-center">
        <div className="mb-8 space-y-2 text-center">
          <h1 className="text-2xl tracking-tight">{stepTitle}</h1>
          <p className="text-muted-foreground text-sm">
            A workspace is where your team organizes conversations and tickets.
          </p>

          <div className="absolute left-0 right-0 top-0 flex items-center justify-center gap-3">
            {steps.map((s, idx) => {
              const completed = s.id < step;
              const active = s.id === step;
              const circleClasses = cn(
                'h-8 w-8 shrink-0 rounded-full border flex items-center justify-center text-sm font-semibold',
                completed
                  ? 'bg-muted border-border text-foreground'
                  : active
                    ? 'bg-card border-border text-foreground'
                    : 'bg-background border-border text-muted-foreground'
              );
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <div className={circleClasses} aria-current={active ? 'step' : undefined}>
                    {completed ? <MonoIcon type="CheckCircle" className="h-4 w-4" /> : s.id}
                  </div>
                  <div
                    className={cn(
                      'text-sm font-medium',
                      completed || active ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {s.label}
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className={cn(
                        'bg-muted h-px flex-1',
                        active || completed ? 'opacity-100' : 'opacity-60'
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="w-full space-y-6">
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-6">
              <div className="flex flex-col space-y-2">
                <label htmlFor="workspace-name" className="text-foreground text-sm font-medium">
                  Workspace name
                </label>
                <Input
                  id="workspace-name"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder={
                    currentUser?.displayName
                      ? `${currentUser.displayName}'s Workspace`
                      : 'e.g. Acme Support'
                  }
                  autoFocus
                  autoComplete="off"
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label htmlFor="company-size" className="text-foreground text-sm font-medium">
                  How large is your company?
                </label>
                <Select
                  value={companySize}
                  onValueChange={setCompanySize}
                  disabled={submitting}
                  items={COMPANY_SIZES.map((s) => ({ value: s.value, label: s.label }))}
                >
                  <SelectTrigger id="company-size" className={'w-full'}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((size) => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="industry" className="text-foreground text-sm font-medium">
                  Company industry
                </label>
                <Select
                  value={industry}
                  onValueChange={setIndustry}
                  disabled={submitting}
                  items={INDUSTRIES.map((ind) => ({ value: ind.value, label: ind.label }))}
                >
                  <SelectTrigger id="industry" className={'w-full'}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map((ind) => (
                      <SelectItem key={ind.value} value={ind.value}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitting || !workspaceName.trim() || authState !== 'signed-in'}
              >
                {submitting ? <Loader /> : 'Continue'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleStep2Submit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="display-name" className="text-foreground mb-2 text-sm font-medium">
                  Display name
                </label>
                <Input
                  id="display-name"
                  value={userDisplayName}
                  onChange={(e) => setUserDisplayName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="off"
                  disabled={submitting}
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="role" className="text-foreground text-sm font-medium">
                  What is your role?
                </label>
                <Select
                  value={role}
                  onValueChange={setRole}
                  disabled={submitting}
                  items={USER_ROLES.map((r) => ({ value: r.value, label: r.label }))}
                >
                  <SelectTrigger id="role">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="use-case" className="text-foreground text-sm font-medium">
                  What do you want to use Dico for?
                </label>
                <Select
                  value={useCaseInput}
                  onValueChange={setUseCaseInput}
                  disabled={submitting}
                  items={USE_CASES.map((uc) => ({ value: uc.value, label: uc.label }))}
                >
                  <SelectTrigger id="use-case" className="w-full">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {USE_CASES.map((uc) => (
                      <SelectItem key={uc.value} value={uc.value}>
                        {uc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="how-heard" className="text-foreground text-sm font-medium">
                  How did you hear about us?
                </label>
                <Select
                  value={howDidYouHear}
                  onValueChange={setHowDidYouHear}
                  disabled={submitting}
                  items={HOW_DID_YOU_HEAR.map((option) => ({
                    value: option.value,
                    label: option.label
                  }))}
                >
                  <SelectTrigger id="how-heard" className="h-9">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOW_DID_YOU_HEAR.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3">
                <Button type="submit" size="lg" className="flex-1" disabled={submitting}>
                  {submitting ? 'Saving…' : 'Continue'}
                </Button>
              </div>

              <p className="text-muted-foreground text-xs">
                This name will show up in your workspace as the member display name.
              </p>
            </form>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-base font-semibold tracking-tight">Integrations</h2>
                <p className="text-muted-foreground text-sm">
                  Connect tools now to start importing and creating tickets. You can also skip and
                  do this later.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="border-border bg-card shadow-xs flex flex-col space-y-3 rounded-xl border p-3">
                  <MonoIcon type="Slack" className="h-5 w-5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Slack</p>
                    <p className="text-muted-foreground text-xs">
                      Import messages and create tickets from Slack.
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    disabled={!workspaceId || integrationLoading === 'slack'}
                    onClick={startSlack}
                  >
                    {integrationLoading === 'slack' ? 'Opening…' : 'Connect'}
                  </Button>
                </div>

                <div className="border-border bg-card shadow-xs flex flex-col space-y-3 rounded-xl border p-3">
                  <MonoIcon type="Github" className="h-5 w-5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">GitHub</p>
                    <p className="text-muted-foreground text-xs">
                      Sync repos and automate ticket workflows.
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    disabled={!workspaceId || integrationLoading === 'github'}
                    onClick={startGithub}
                  >
                    {integrationLoading === 'github' ? 'Opening…' : 'Connect'}
                  </Button>
                </div>

                <div className="border-border bg-card shadow-xs flex flex-col space-y-3 rounded-xl border p-3">
                  <MonoIcon type="Discord" className="h-5 w-5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Discord</p>
                    <p className="text-muted-foreground text-xs">
                      Add the bot and start creating tickets.
                    </p>
                  </div>
                  <Button
                    className="w-full"
                    disabled={!workspaceId || integrationLoading === 'discord'}
                    onClick={startDiscord}
                  >
                    {integrationLoading === 'discord' ? 'Opening…' : 'Connect'}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button type="button" size="lg" className="flex-1" onClick={finish}>
                  Continue
                </Button>
              </div>

              <p className="text-muted-foreground text-xs">
                You can manage integrations later under Workspace settings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
